import { type FC, type ReactElement, useCallback, useEffect, useRef, useState } from "react";
import { throttle } from "throttle-debounce";
import { assignInlineVars } from "@vanilla-extract/dynamic";
import { Icon } from "@root/shared/components/Icon";
import { ReadMoreButtonHeightProvider } from "./useReadMoreButtonHeight";
import * as styles from "./styles.css";

const POLLING_INTERVAL_MS = 100; // チェック間隔

const useMonitorHeight = (ref: React.RefObject<HTMLDivElement>) => {
  const [isHeightReady, setIsHeightReady] = useState(false);

  useEffect(() => {
    if (!ref.current) {
      // refが存在しない場合はそのまま終了
      return;
    }

    const checkHeight = () => {
      if (ref.current && ref.current.offsetHeight > 0) {
        setIsHeightReady(true); // 高さが設定されたらフラグを立てる
      }
    };
    checkHeight();
    if (isHeightReady) return;
    const intervalId = setInterval(checkHeight, POLLING_INTERVAL_MS);
    // eslint-disable-next-line consistent-return
    return () => {
      clearInterval(intervalId);
    };
  }, [ref, isHeightReady]);

  return isHeightReady;
};

type Props = {
  containerMaxHeight?: number;
  children: ReactElement;
  size: "small" | "large";
};

const SMALL_CONTAINER_MAX_HEIGHT = 240;
const LARGE_CONTAINER_MAX_HEIGHT = 200;
const THROTTLE_MS = 200;

export const ReadMoreButton: FC<Props> = ({ size, containerMaxHeight, children }) => {
  const initialContainerHeight =
    containerMaxHeight ??
    (size === "small" ? SMALL_CONTAINER_MAX_HEIGHT : LARGE_CONTAINER_MAX_HEIGHT);

  const [isContainerOpened, setIsContainerOpened] = useState(false);
  const [containerHeight, setContainerHeight] = useState<number | undefined>(
    initialContainerHeight,
  );
  const [contentsHeight, setContentsHeight] = useState<number>();
  const contentsRef = useRef<HTMLDivElement>(null);

  // フックを使って高さの準備が整ったか確認
  const isHeightReady = useMonitorHeight(contentsRef);

  const openContainerIfNeeded = useCallback(() => {
    if (contentsRef?.current) {
      const { offsetHeight } = contentsRef.current;
      setContentsHeight(offsetHeight);

      // 閉じないサイズの場合は開く
      if (containerHeight !== undefined && offsetHeight <= containerHeight) {
        setIsContainerOpened(true);
      }
    }
  }, [containerHeight]);

  const handleClick = useCallback(() => {
    setIsContainerOpened(true);
  }, []);

  const getMaxHeight = () => {
    if (
      !isContainerOpened &&
      contentsHeight !== undefined &&
      containerHeight !== undefined &&
      contentsHeight > containerHeight
    ) {
      return `${containerHeight}px`;
    }
    return "none";
  };

  useEffect(() => {
    if (containerMaxHeight !== undefined && containerMaxHeight !== containerHeight) {
      setContainerHeight(containerMaxHeight);
    }
  }, [containerMaxHeight, containerHeight]);

  useEffect(() => {
    if (!isContainerOpened && isHeightReady) {
      openContainerIfNeeded();
      const openContainerIfNeededThrottle = throttle(THROTTLE_MS, openContainerIfNeeded);
      window.addEventListener("resize", openContainerIfNeededThrottle);

      return () => {
        window.removeEventListener("resize", openContainerIfNeededThrottle);
      };
    }
    return undefined;
  }, [isContainerOpened, openContainerIfNeeded, children, isHeightReady]);

  return (
    <div
      className={styles.container}
      style={assignInlineVars({
        [styles.maxHeight]: getMaxHeight(),
        [styles.overflow]: isContainerOpened ? "visible" : "hidden",
      })}
    >
      <div className={styles.contents} ref={contentsRef}>
        <ReadMoreButtonHeightProvider value={setContainerHeight}>
          {children}
        </ReadMoreButtonHeightProvider>
      </div>
      {!isContainerOpened && (
        <div
          className={styles.buttonOuter({
            size,
          })}
        >
          <button
            type="button"
            className={`
              ${styles.textButton({
                size,
              })}
              ${styles.emptyClassForUnderline}
            `}
            onClick={handleClick}
          >
            {size === "large" && (
              <span className={styles.iconOuter}>
                <Icon name="add-more" fontSize="16px" />
              </span>
            )}
            <span className={styles.text}>続きを見る</span>
          </button>
        </div>
      )}
    </div>
  );
};
